import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  lazy,
  Suspense,
} from 'react';
import useAuth from 'auth/auth';
import {
  Product,
  UploadDifferences,
  ProductChangeRecord,
  PutProductConflict,
  PutProductMultiResult,
  PutProductSuccess,
} from 'services/api/domains/products/types';
import { useManufacturersPage } from '../services/api/domains/manufacturers/hooks';
import {
  useDeleteProduct,
  useUpdateProduct,
  useProduct,
  useUploadProducts,
  useCommitProductChanges,
} from '../services/api/domains/products/hooks';
import { ApiStateBase } from 'services/api/types';
import ProductModal from 'components/modals/ProductModal';
import ConflictModal from 'components/modals/ConflictModal';
import MultiResultModal from 'components/modals/MultiResultModal';
import CircularProgress from '@mui/material/CircularProgress';
import { ModalMode } from 'components/modals/hooks/useEditableModal';
import { ProductsRowGroupingDataGrid } from '../components/ProductsRowGroupingDataGrid';
import { FlatProduct } from 'services/api/domains/products/types';
import {
  GRAY_100,
  GRAY_200,
  GRAY_300,
  GRAY_400,
  GRAY_50,
  GRAY_500,
  GRAY_600,
  GRAY_700,
} from 'theme/grayScale';
import { COLOR_DANGER_BG, COLOR_WARNING_BG } from 'theme/colors';
import { useABTest } from '../contexts/ABTestContext';

// Lazy load UploadProductsModal to defer XLSX library loading (~600KB)
const UploadProductsModal = lazy(
  () => import('components/modals/UploadProductsModal')
);

interface ProductsReviewProps {
  deleteAllStatus: ApiStateBase<string>;
  /** Deep-link filter from the Manufacturers view (Task 2.2) — threaded straight through to the grid. */
  initialManufacturerFilter?: { manufacturerId: number } | null;
  /** Called once the initial filter above has been applied, so the parent can clear its pending state. */
  onInitialFilterConsumed?: () => void;
  /** Opens the given product's manufacturer, filtered (Task 4) — threaded straight through to the grid. */
  onViewManufacturer?: (
    manufacturerId: number,
    manufacturerName?: string
  ) => void;
  /** Navigates to the Product Uploads view (issue #349) -- passed only when
   * the user has permission, same convention as onEdit/onViewManufacturer. */
  onUpload?: () => void;
  /** Deep-link from the upload batch detail page's created-row indicator
   * (issue #349, Sub-task 11.3) -- opens the given product's VIEW modal on
   * mount, same lazy-init + remount-via-key convention as
   * initialManufacturerFilter (Task 2.2). Doesn't require the product to
   * already be loaded in the grid -- selectedProductId drives its own
   * useProduct() fetch below. */
  initialProductIdToOpen?: number | null;
  /** Called once the deep-link above has been applied. */
  onInitialProductIdConsumed?: () => void;
}

/** Extends the server conflict payload with the frontend-resolved manufacturer ID and new name. */
interface ConflictState extends PutProductConflict {
  manufacturerId: number;
  newManProductName?: string | null;
}

/** Extends the server multi-result payload with the frontend-resolved manufacturer ID. */
interface MultiResultState extends PutProductMultiResult {
  manufacturerId: number;
}

// Type for normalized change items
interface NormalizedChange {
  field: string;
  oldValue: string | undefined;
  newValue: string | undefined;
}

const ProductsReview: React.FC<ProductsReviewProps> = ({
  deleteAllStatus,
  initialManufacturerFilter,
  onInitialFilterConsumed,
  onViewManufacturer,
  onUpload,
  initialProductIdToOpen,
  onInitialProductIdConsumed,
}) => {
  const { isViewer } = useAuth();
  const { getTestValue } = useABTest();
  const hideProductType = getTestValue('hide_product_type');

  const highLimitParam = useMemo(() => ({ limit: 1000 }), []);

  // Fetch ALL manufacturers without pagination for the modal options
  const manufacturersManager = useManufacturersPage(highLimitParam);

  const uploadProductsMutation = useUploadProducts();
  const commitChangesMutation = useCommitProductChanges();
  const deleteProductMutation = useDeleteProduct();
  const updateProductMutation = useUpdateProduct();

  const [openProductModal, setOpenProductModal] = useState<ModalMode | null>(
    () => (initialProductIdToOpen ? 'VIEW' : null)
  );
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  // State for dynamic row grouping modal fetch path
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    () => initialProductIdToOpen ?? null
  );

  useEffect(() => {
    if (initialProductIdToOpen) {
      onInitialProductIdConsumed?.();
    }
    // Full deps, not a mount-only suppression (Copilot review, PR #735) --
    // ProductsReview already remounts via `key={productsFilterNonce}` on
    // every navigateToProductAndOpen call (Task 2.2's convention), so this
    // effect only ever runs once per navigation regardless; re-running it
    // if the props somehow changed without a remount is harmless too --
    // onInitialProductIdConsumed is idempotent once already cleared upstream.
  }, [initialProductIdToOpen, onInitialProductIdConsumed]);
  const [pendingModalRow, setPendingModalRow] = useState<FlatProduct | null>(
    null
  );
  // Pending CAS registry outcome state — populated when PUT /products/:id
  // returns a non-save response (conflict or multi_result). Tasks 5 and 6
  // render ConflictModal / MultiResultModal from this data.
  const [pendingConflict, setPendingConflict] = useState<ConflictState | null>(
    null
  );
  const [pendingMultiResult, setPendingMultiResult] =
    useState<MultiResultState | null>(null);
  // Stores the original editedProduct when multi_result comes back so that the
  // "Select one" re-PUT path can replay the save with selectedCasRn.
  const [pendingEditPayload, setPendingEditPayload] = useState<Product | null>(
    null
  );
  // Incrementing counter passed to ProductsRowGroupingDataGrid to trigger a
  // flat-data refetch after a successful product save.
  const [flatGridRefreshTrigger, setFlatGridRefreshTrigger] = useState(0);

  // Fetch full product data when a row is selected in the dynamic row grouping grid
  const modalProductQuery = useProduct(selectedProductId ?? 0, {
    enabled: selectedProductId !== null,
  });
  const modalProductStatus = modalProductQuery.status;
  const modalProductData: Product | undefined =
    modalProductStatus === 'success'
      ? (
          modalProductQuery as {
            status: 'success';
            data: Product;
            refetch: () => Promise<void>;
            invalidate: () => void;
          }
        ).data
      : undefined;

  // Tracks the product id for which a fresh fetch cycle has started (loading state seen).
  // Guards against the useQuery hook returning a stale 'success' result from a prior
  // fetch cycle when the modal is reopened before the new fetch completes — which would
  // cause selectedProduct to be set to old data, showing the wrong Product Type.
  const modalFetchIdRef = useRef<number | null>(null);

  // Clear stale product data immediately when a new product is selected,
  // so the modal never shows the previous product's data while the fetch is in-flight.
  useEffect(() => {
    setSelectedProduct(null);
  }, [selectedProductId]);

  // Sync full product data into selectedProduct once the fetch resolves
  useEffect(() => {
    if (selectedProductId === null) {
      modalFetchIdRef.current = null;
      return;
    }
    if (modalProductStatus === 'loading') {
      // A fresh fetch is underway for this product — record the id so we know
      // the subsequent 'success' result is fresh (not from a previous cycle).
      modalFetchIdRef.current = selectedProductId;
    }
    if (
      modalProductStatus === 'success' &&
      modalProductData &&
      modalFetchIdRef.current === selectedProductId
    ) {
      setSelectedProduct(modalProductData);
    } else if (modalProductStatus === 'error' && pendingModalRow) {
      // Error fallback: open modal with basic row data so user is not stranded
      setSelectedProduct({
        product_id: pendingModalRow.product_id,
        product_cas_name: pendingModalRow.product_cas_name,
        cas_number: pendingModalRow.cas_number ?? null,
        is_cas_verified: pendingModalRow.is_cas_verified ?? false,
        preferred_product_name: pendingModalRow.preferred_product_name ?? '',
        note: '',
        molecular_formula: '',
        molecular_mass: '',
        uri: '',
        inchi: '',
        inchi_key: '',
        smile: '',
        canonical_smile: '',
        synonyms: [],
        replaced_rns: [],
        manufacturers: [],
        facilities: [],
        grades: [],
        therapeutic_areas: [],
        user_id: '',
        created_date: new Date(),
        modified_date: new Date(),
      });
    }
  }, [
    selectedProductId,
    modalProductStatus,
    modalProductData,
    pendingModalRow,
  ]);

  const [openMfgPick, setOpenMfgPick] = useState(false);
  const [chosenManufacturer, setChosenManufacturer] = useState<number | null>(
    null
  );

  const [diffData, setDiffData] = useState<UploadDifferences | null>(null);
  const [openDiffModal, setOpenDiffModal] = useState(false);

  useEffect(() => {
    if (deleteAllStatus.status === 'success') {
      setFlatGridRefreshTrigger(prev => prev + 1);
    }
  }, [deleteAllStatus.status]);

  const handleConfirmManufacturer = async (
    chosenManufacturer: number | null,
    uploadFile: File | null
  ) => {
    if (!uploadFile || !chosenManufacturer)
      return alert('❌ Select manufacturer and file.');
    try {
      setChosenManufacturer(chosenManufacturer);
      const result = await uploadProductsMutation.mutate({
        file: uploadFile,
        manufacturer_ids: [chosenManufacturer],
        facility_ids: [],
      });
      if (result.hasDifferences && result.differences) {
        setDiffData(result.differences);
        setOpenDiffModal(true);
      } else {
        alert('✅ Upload complete.');
        setOpenMfgPick(false);
        setFlatGridRefreshTrigger(prev => prev + 1);
      }
    } catch (error) {
      console.error('❌ Upload failed:', error);
      alert('Upload failed. Please try again.');
    }
  };

  const handleCommitChanges = async () => {
    if (!chosenManufacturer || !diffData) return;
    try {
      await commitChangesMutation.mutate({
        manufacturer_ids: [chosenManufacturer],
        facility_ids: [],
        differences: diffData,
      });
      alert('✅ Changes committed.');
      setOpenDiffModal(false);
      setDiffData(null);
      setOpenMfgPick(false);
      setFlatGridRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('❌ Commit failed:', error);
      alert('Commit failed. Please try again.');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete product?')) return;
    await deleteProductMutation.mutate(id);
    setFlatGridRefreshTrigger(prev => prev + 1);
  };

  const handleSaveEdit = async (editedProduct: Product) => {
    if (!editedProduct || !editedProduct.product_id) return;
    try {
      const result = await updateProductMutation.mutate({
        ...editedProduct,
        manufacturers: Array.isArray(editedProduct.manufacturers)
          ? editedProduct.manufacturers.map(m => ({
              manufacturer_id: m.manufacturer_id,
              manufacturing_status: m.manufacturing_status || null,
              ...(m.grade_ids !== undefined ? { grade_ids: m.grade_ids } : {}),
              man_product_name: m.man_product_name ?? null,
            }))
          : [],
        facility_ids: Array.isArray(editedProduct.facilities)
          ? editedProduct.facilities.map(f => f.facility_id)
          : [],
        therapeutic_area_ids: Array.isArray(editedProduct.therapeutic_areas)
          ? editedProduct.therapeutic_areas.map(ta => ta.therapeutic_area_id)
          : [],
      });

      // Route based on registry outcome — non-save responses keep the modal open
      if ('status' in result) {
        if (result.status === 'conflict') {
          // Identify which manufacturer's name change triggered the conflict
          const originalManufacturers = selectedProduct?.manufacturers ?? [];
          const changedMfr = editedProduct.manufacturers.find(m => {
            const orig = originalManufacturers.find(
              om => om.manufacturer_id === m.manufacturer_id
            );
            return (
              (m.man_product_name ?? null) !== (orig?.man_product_name ?? null)
            );
          });
          setPendingConflict({
            ...result,
            manufacturerId:
              changedMfr?.manufacturer_id ??
              editedProduct.manufacturers[0]?.manufacturer_id ??
              0,
            newManProductName: changedMfr?.man_product_name ?? null,
          });
          return; // ConflictModal renders (Task 5.1)
        }
        if (result.status === 'multi_result') {
          const originalManufacturers = selectedProduct?.manufacturers ?? [];
          const changedMfr = editedProduct.manufacturers.find(m => {
            const orig = originalManufacturers.find(
              om => om.manufacturer_id === m.manufacturer_id
            );
            return (
              (m.man_product_name ?? null) !== (orig?.man_product_name ?? null)
            );
          });
          setPendingMultiResult({
            ...result,
            manufacturerId:
              changedMfr?.manufacturer_id ??
              editedProduct.manufacturers[0]?.manufacturer_id ??
              0,
          });
          setPendingEditPayload(editedProduct);
          return; // MultiResultModal renders (Task 6.1)
        }
      }

      // Normal save — show warning if CasNoResult downgraded verification
      const successResult = result as PutProductSuccess;
      if (successResult.warning) {
        // TODO: replace alert with a toast/notification component
        alert(`⚠️  ${successResult.warning}`);
      }

      setOpenProductModal(null);
      setSelectedProduct(null);
      setSelectedProductId(null);
      setPendingModalRow(null);
      setPendingConflict(null);
      setPendingMultiResult(null);
      setPendingEditPayload(null);
      setFlatGridRefreshTrigger(prev => prev + 1);
    } catch (error) {
      // 503 Unavailable: registry down — block with targeted message, keep modal open
      const apiError = error as {
        response?: { status?: number; data?: { status?: string } };
      };
      if (
        apiError?.response?.status === 503 ||
        apiError?.response?.data?.status === 'unavailable'
      ) {
        alert('Registry service unavailable — try again later.');
      } else {
        console.error('❌ Update failed:', error);
        alert('❌ Failed to update product. Please try again.');
      }
    }
  };

  /**
   * Re-PUT with a specific CAS RN chosen by the user in MultiResultModal
   * (unverified path only). The backend skips registry search when
   * selectedCasRn is present and enriches with the chosen entity's data.
   */
  const handleMultiSelectOne = async (casRn: string) => {
    if (!pendingEditPayload) return;
    const ep = pendingEditPayload;
    try {
      await updateProductMutation.mutate({
        ...ep,
        manufacturers: Array.isArray(ep.manufacturers)
          ? ep.manufacturers.map(m => ({
              manufacturer_id: m.manufacturer_id,
              manufacturing_status: m.manufacturing_status || null,
              ...(m.grade_ids !== undefined ? { grade_ids: m.grade_ids } : {}),
              man_product_name: m.man_product_name ?? null,
            }))
          : [],
        facility_ids: Array.isArray(ep.facilities)
          ? ep.facilities.map(f => f.facility_id)
          : [],
        therapeutic_area_ids: Array.isArray(ep.therapeutic_areas)
          ? ep.therapeutic_areas.map(ta => ta.therapeutic_area_id)
          : [],
        selectedCasRn: casRn,
      });
      setOpenProductModal(null);
      setSelectedProduct(null);
      setSelectedProductId(null);
      setPendingModalRow(null);
      setPendingConflict(null);
      setPendingMultiResult(null);
      setPendingEditPayload(null);
      setFlatGridRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('❌ Re-PUT with selected CAS RN failed:', error);
      alert('❌ Failed to save with selected entry. Please try again.');
    }
  };

  // normalize Changed Products
  const normalizeChangedProducts = (
    items: ProductChangeRecord[]
  ): NormalizedChange[] =>
    (items || []).flatMap((c: ProductChangeRecord) => {
      const before = c.before;
      const after = c.after;
      const diffs: NormalizedChange[] = [];

      if (before.product_cas_name !== after['PRODUCT NAME'])
        diffs.push({
          field: 'Product Name',
          oldValue: before.product_cas_name,
          newValue: after['PRODUCT NAME'],
        });
      if (before.cas_number !== after['CAS NUMBER'])
        diffs.push({
          field: 'CAS #',
          oldValue: before.cas_number,
          newValue: after['CAS NUMBER'],
        });
      // TODO: Update diff utility to handle grade and therapeutic area names
      // Note: grade_name and therapeutic_area_name are not available in ExistingProductRecord
      // The API returns grade_id and therapeutic_area_id instead
      // if (after['GRADE'])
      //   diffs.push({
      //     field: 'Grade',
      //     oldValue: 'Previous Grade ID: ' + before.grade_id,
      //     newValue: after['GRADE'],
      //   });
      // if (after['THERAPEUTIC AREA'])
      //   diffs.push({
      //     field: 'Therapeutic Area',
      //     oldValue: 'Previous TA ID: ' + before.therapeutic_area_id,
      //     newValue: after['THERAPEUTIC AREA'],
      //   });
      if (before.note !== after['NOTE'])
        diffs.push({
          field: 'Note',
          oldValue: before.note,
          newValue: after['NOTE'],
        });
      return diffs;
    });

  const changedItems = normalizeChangedProducts(diffData?.changed || []);

  return (
    <div style={styles.pageContainerStyle}>
      <div style={styles.tableContainerStyle} data-layout-role="table-body">
        <ProductsRowGroupingDataGrid
          refreshTrigger={flatGridRefreshTrigger}
          initialManufacturerFilter={initialManufacturerFilter}
          onInitialFilterConsumed={onInitialFilterConsumed}
          onViewManufacturer={onViewManufacturer}
          onUpload={onUpload}
          onInfo={row => {
            setSelectedProductId(row.product_id);
            setPendingModalRow(row);
            setOpenProductModal('VIEW');
          }}
          onEdit={
            !isViewer
              ? row => {
                  setSelectedProductId(row.product_id);
                  setPendingModalRow(row);
                  setOpenProductModal('EDIT');
                }
              : undefined
          }
          onDelete={
            !isViewer
              ? row => {
                  handleDelete(row.product_id);
                }
              : undefined
          }
        />

        {/* Upload Modal */}
        {openMfgPick && (
          <Suspense
            fallback={
              <div style={styles.modalOverlayStyle}>
                <div style={{ color: '#666', padding: '2rem' }}>
                  Loading upload...
                </div>
              </div>
            }>
            <UploadProductsModal
              isLoading={uploadProductsMutation.status === 'loading'}
              confirmManufacturer={handleConfirmManufacturer}
              closeModal={() => setOpenMfgPick(false)}
            />
          </Suspense>
        )}

        {/* Diff Modal */}
        {openDiffModal && diffData && (
          <div style={styles.modalOverlayStyle}>
            <div style={styles.diffModalContentStyle}>
              <h2 style={styles.diffModalTitleStyle}>Differences Found</h2>

              {/* Summary */}
              <div style={styles.diffSummaryStyle}>
                <p>
                  <strong>New Products:</strong> {diffData.new?.length || 0}
                </p>
                <p>
                  <strong>Changed Products:</strong>{' '}
                  {diffData.changed?.length || 0}
                </p>
                <p>
                  <strong>Deleted Products:</strong>{' '}
                  {diffData.deleted?.length || 0}
                </p>
              </div>

              {/* New Products */}
              {diffData.new && diffData.new.length > 0 && (
                <div style={styles.diffSectionStyle}>
                  <h3 style={styles.newProductsHeaderStyle}>
                    New Products ({diffData.new.length})
                  </h3>
                  <table style={styles.diffTableStyle}>
                    <thead style={styles.newProductsTheadStyle}>
                      <tr>
                        <th style={styles.diffTableHeaderStyle}>
                          Product Name
                        </th>
                        <th style={styles.diffTableHeaderStyle}>CAS Number</th>
                        {!hideProductType && (
                          <th style={styles.diffTableHeaderStyle}>
                            Product Type
                          </th>
                        )}
                        <th style={styles.diffTableHeaderStyle}>
                          Therapeutic Area
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {diffData.new.map((item, i) => (
                        <tr
                          key={
                            item['PRODUCT NAME'] +
                            '-' +
                            (item['CAS NUMBER'] || '') +
                            '-' +
                            i
                          }>
                          <td style={styles.diffTableCellStyle}>
                            {item['PRODUCT NAME']}
                          </td>
                          <td style={styles.diffTableCellStyle}>
                            {item['CAS NUMBER'] || ''}
                          </td>
                          {!hideProductType && (
                            <td style={styles.diffTableCellStyle}>
                              {item['GRADE'] || ''}
                            </td>
                          )}
                          <td style={styles.diffTableCellStyle}>
                            {item['THERAPEUTIC AREA'] || ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Changed Products */}
              {changedItems.length > 0 && (
                <div style={styles.diffSectionStyle}>
                  <h3 style={styles.changedProductsHeaderStyle}>
                    Changed Products ({changedItems.length} changes)
                  </h3>
                  <table style={styles.diffTableStyle}>
                    <thead style={styles.changedProductsTheadStyle}>
                      <tr>
                        <th style={styles.diffTableHeaderStyle}>Field</th>
                        <th style={styles.diffTableHeaderStyle}>Old</th>
                        <th style={styles.diffTableHeaderStyle}>New</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changedItems.map((c, i) => (
                        <tr
                          key={
                            c.field +
                            '-' +
                            (c.oldValue ?? '') +
                            '-' +
                            (c.newValue ?? '') +
                            '-' +
                            i
                          }>
                          <td style={styles.diffTableCellStyle}>{c.field}</td>
                          <td style={styles.diffTableCellOldStyle}>
                            {c.oldValue}
                          </td>
                          <td style={styles.diffTableCellNewStyle}>
                            {c.newValue}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Deleted Products */}
              {diffData.deleted && diffData.deleted.length > 0 && (
                <div style={styles.diffSectionStyle}>
                  <h3 style={styles.deletedProductsHeaderStyle}>
                    Deleted Products ({diffData.deleted.length})
                  </h3>
                  <table style={styles.diffTableStyle}>
                    <thead style={styles.deletedProductsTheadStyle}>
                      <tr>
                        <th style={styles.diffTableHeaderStyle}>
                          Product Name
                        </th>
                        <th style={styles.diffTableHeaderStyle}>CAS Number</th>
                        <th style={styles.diffTableHeaderStyle}>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diffData.deleted.map((item, i) => (
                        <tr
                          key={
                            item.product_cas_name +
                            '-' +
                            (item.cas_number || '') +
                            '-' +
                            i
                          }>
                          <td style={styles.diffTableCellStyle}>
                            {item.product_cas_name}
                          </td>
                          <td style={styles.diffTableCellStyle}>
                            {item.cas_number || ''}
                          </td>
                          <td style={styles.diffTableCellStyle}>
                            {item.note || ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={styles.diffModalActionsStyle}>
                <button
                  onClick={() => setOpenDiffModal(false)}
                  style={styles.diffCancelButtonStyle}>
                  Cancel
                </button>
                <button
                  onClick={handleCommitChanges}
                  style={styles.diffCommitButtonStyle}
                  disabled={commitChangesMutation.status === 'loading'}>
                  {commitChangesMutation.status === 'loading'
                    ? 'Applying…'
                    : 'Apply'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {/* {productsLoading && (
          <div style={styles.loadingContainerStyle}>
            <span style={styles.loadingTextStyle}>Loading products...</span>
          </div>
        )} */}

        {/* All your existing Upload, Diff, Display, and Edit Modals remain below, unchanged */}

        {/* Loading overlay — shown while useProduct fetch is in progress for the dynamic row grouping modal */}
        {openProductModal !== null &&
          selectedProductId !== null &&
          !selectedProduct && (
            <div style={styles.modalOverlayStyle}>
              <div style={styles.productLoadingContainerStyle}>
                <CircularProgress />
              </div>
            </div>
          )}

        {/* Display Modal */}
        {openProductModal !== null && selectedProduct && (
          <ProductModal
            product={selectedProduct}
            initialMode={openProductModal}
            save={handleSaveEdit}
            close={() => {
              setOpenProductModal(null);
              setSelectedProductId(null);
              setPendingModalRow(null);
              setPendingConflict(null);
              setPendingMultiResult(null);
              setPendingEditPayload(null);
            }}
            allManufacturers={manufacturersManager.data}
          />
        )}
        {/* ConflictModal — Task 5.1: stacked above ProductModal on CAS conflict */}
        {pendingConflict && selectedProduct && (
          <ConflictModal
            conflict={pendingConflict}
            originalProductId={selectedProduct.product_id}
            manufacturerId={pendingConflict.manufacturerId}
            newManProductName={pendingConflict.newManProductName ?? null}
            currentProductName={selectedProduct.product_cas_name}
            currentCasNumber={selectedProduct.cas_number ?? null}
            currentManProductName={
              selectedProduct.manufacturers.find(
                m => m.manufacturer_id === pendingConflict.manufacturerId
              )?.man_product_name ?? null
            }
            onForkSuccess={() => {
              setOpenProductModal(null);
              setSelectedProduct(null);
              setSelectedProductId(null);
              setPendingModalRow(null);
              setPendingConflict(null);
              setPendingMultiResult(null);
              setPendingEditPayload(null);
              setFlatGridRefreshTrigger(prev => prev + 1);
            }}
            onCancel={() => setPendingConflict(null)}
          />
        )}
        {/* MultiResultModal — Task 6.1: stacked above ProductModal on multi_result */}
        {pendingMultiResult && selectedProduct && (
          <MultiResultModal
            multiResult={pendingMultiResult}
            originalProductId={selectedProduct.product_id}
            manufacturerId={pendingMultiResult.manufacturerId}
            onSelectOne={handleMultiSelectOne}
            onForkAllSuccess={() => {
              setOpenProductModal(null);
              setSelectedProduct(null);
              setSelectedProductId(null);
              setPendingModalRow(null);
              setPendingConflict(null);
              setPendingMultiResult(null);
              setPendingEditPayload(null);
              setFlatGridRefreshTrigger(prev => prev + 1);
            }}
            onCancel={() => {
              setPendingMultiResult(null);
              setPendingEditPayload(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  titleStyle: {
    fontSize: '1.25rem', // text-xl
    fontWeight: 'bold',
  } as React.CSSProperties,
  searchUploadContainerStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  modalOverlayStyle: {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    backgroundColor: 'rgba(0, 0, 0, 0.5)', // bg-black bg-opacity-50
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
  },
  modalTitleStyle: {
    fontSize: '18px', // text-lg
    fontWeight: 'bold', // font-bold
    marginBottom: '16px', // mb-4
  },
  manufacturerListStyle: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px', // space-y-2
    maxHeight: '240px', // max-h-60
    overflowY: 'auto', // overflow-y-auto
  },
  manufacturerButtonStyle: {
    display: 'block',
    width: '100%',
    textAlign: 'left' as const,
    padding: '8px 12px', // px-3 py-2
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px', // rounded
    cursor: 'pointer',
  },
  modalButtonContainerStyle: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px', // space-x-2
    marginTop: '16px', // mt-4
  },
  cancelButtonStyle: {
    padding: '8px 16px', // px-4 py-2
    backgroundColor: GRAY_300, // bg-gray-300
    color: GRAY_700, // text-gray-700
    borderRadius: '4px', // rounded
    border: 'none',
    cursor: 'pointer',
  },
  diffModalContentStyle: {
    backgroundColor: '#ffffff', // bg-white
    padding: '24px', // p-6
    borderRadius: '8px', // rounded-lg
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', // shadow-lg
    width: '900px', // w-[900px]
    maxHeight: '80vh', // max-h-[80vh]
    overflowY: 'auto', // overflow-y-auto
  },
  diffModalHeaderStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px', // mb-4
  },
  diffModalTitleStyle: {
    fontSize: '20px', // text-xl
    fontWeight: 'bold', // font-bold
  },
  closeButtonStyle: {
    color: GRAY_500, // text-gray-500
    fontSize: '20px', // text-xl
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
  },
  buttonEnabled: {
    color: '#d97706', // text-yellow-600
    cursor: 'pointer',
  },
  buttonDisabled: {
    color: GRAY_400, // text-gray-400
    cursor: 'not-allowed',
  },
  deleteButtonEnabled: {
    color: '#dc2626', // text-red-600
    cursor: 'pointer',
  },
  diffSummaryStyle: {
    marginBottom: '16px', // mb-4
    padding: '12px', // p-3
    backgroundColor: GRAY_100, // bg-gray-100
    borderRadius: '4px', // rounded
  },
  diffSectionStyle: {
    marginBottom: '16px', // mb-4
  },
  newProductsHeaderStyle: {
    fontWeight: '600', // font-semibold
    color: '#059669', // text-green-600
    marginBottom: '8px', // mb-2
  },
  diffTableStyle: {
    width: '100%',
    border: `1px solid ${GRAY_300}`,
    fontSize: '14px', // text-sm
    marginBottom: '16px', // mb-4
  },
  newProductsTheadStyle: {
    backgroundColor: '#d1fae5', // bg-green-100
  },
  diffTableHeaderStyle: {
    border: `1px solid ${GRAY_300}`,
    padding: '8px', // p-2
    textAlign: 'left' as const,
  },
  diffTableCellStyle: {
    border: `1px solid ${GRAY_300}`,
    padding: '8px', // p-2
  },
  changedProductsHeaderStyle: {
    fontWeight: '600', // font-semibold
    color: '#d97706', // text-yellow-600
    marginBottom: '8px', // mb-2
  },
  changedProductsTheadStyle: {
    backgroundColor: COLOR_WARNING_BG, // bg-yellow-100
  },
  diffTableCellOldStyle: {
    border: `1px solid ${GRAY_300}`,
    padding: '8px', // p-2
    color: '#dc2626', // text-red-600
  },
  diffTableCellNewStyle: {
    border: `1px solid ${GRAY_300}`,
    padding: '8px', // p-2
    color: '#059669', // text-green-600
  },
  deletedProductsHeaderStyle: {
    fontWeight: '600', // font-semibold
    color: '#dc2626', // text-red-600
    marginBottom: '8px', // mb-2
  },
  deletedProductsTheadStyle: {
    backgroundColor: COLOR_DANGER_BG, // bg-red-100
  },
  diffModalActionsStyle: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px', // space-x-2
  },
  diffCancelButtonStyle: {
    backgroundColor: GRAY_400, // bg-gray-400
    color: '#ffffff', // text-white
    padding: '8px 16px', // px-4 py-2
    borderRadius: '4px', // rounded
    border: 'none',
    cursor: 'pointer',
  },
  diffCommitButtonStyle: {
    backgroundColor: '#059669', // bg-green-600
    color: '#ffffff', // text-white
    padding: '8px 16px', // px-4 py-2
    borderRadius: '4px', // rounded
    border: 'none',
    cursor: 'pointer',
  },
  loadingContainerStyle: {
    textAlign: 'center',
    padding: '16px 0', // py-4
  },
  loadingTextStyle: {
    color: GRAY_600, // text-gray-600
  },
  errorContainerStyle: {
    backgroundColor: '#fecaca', // bg-red-100
    border: '1px solid #f87171', // border-red-400
    color: '#b91c1c', // text-red-700
    padding: '12px 16px', // px-4 py-3
    borderRadius: '4px', // rounded
    marginBottom: '16px', // mb-4
  },
  mainTableStyle: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    border: `1px solid ${GRAY_300}`, // border-gray-300
  },
  mainTableHeaderStyle: {
    backgroundColor: GRAY_100, // bg-gray-100
  },
  sortableHeaderStyle: {
    border: `1px solid ${GRAY_300}`,
    padding: '8px', // p-2
    textAlign: 'left' as const,
    cursor: 'pointer',
  },
  mainTableNonSortableHeaderStyle: {
    border: `1px solid ${GRAY_300}`,
    padding: '8px', // p-2
    textAlign: 'left' as const,
  },
  mainTableCellStyle: {
    border: `1px solid ${GRAY_300}`,
    padding: '8px', // p-2
  },
  actionButtonsContainerStyle: {
    display: 'flex',
    gap: '8px', // space-x-2
  },
  infoButtonStyle: {
    color: '#2563eb', // text-blue-600
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '0',
  },
  paginationContainerStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '16px', // mt-4
  },
  paginationButtonStyle: {
    padding: '4px 12px', // px-3 py-1
    backgroundColor: GRAY_200, // bg-gray-200
    borderRadius: '4px', // rounded
    border: 'none',
    cursor: 'pointer',
  },
  displayModalContentStyle: {
    backgroundColor: '#ffffff',
    padding: '24px', // p-6
    borderRadius: '8px', // rounded-lg
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', // shadow-lg
    width: '384px', // w-96
  },
  displayModalCloseButtonStyle: {
    padding: '8px 16px', // px-4 py-2
    backgroundColor: GRAY_400, // bg-gray-400
    color: '#ffffff', // text-white
    borderRadius: '4px', // rounded
    marginTop: '16px', // mt-4
    border: 'none',
    cursor: 'pointer',
  },
  editModalContentStyle: {
    backgroundColor: '#ffffff',
    padding: '24px', // p-6
    borderRadius: '8px', // rounded-lg
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', // shadow-lg
    width: '384px', // w-96
  },
  editModalInputStyle: {
    border: `1px solid ${GRAY_300}`,
    padding: '8px', // p-2
    marginBottom: '8px', // mb-2
    width: '100%',
  },
  editModalTextareaStyle: {
    border: `1px solid ${GRAY_300}`,
    padding: '8px', // p-2
    marginBottom: '8px', // mb-2
    width: '100%',
    resize: 'vertical' as const,
  },
  editModalActionsStyle: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px', // space-x-2
  },
  editModalCancelButtonStyle: {
    padding: '8px 16px', // px-4 py-2
    backgroundColor: GRAY_400, // bg-gray-400
    color: '#ffffff', // text-white
    borderRadius: '4px', // rounded
    border: 'none',
    cursor: 'pointer',
  },
  editModalSaveButtonStyle: {
    padding: '8px 16px', // px-4 py-2
    backgroundColor: '#059669', // bg-green-600
    color: '#ffffff', // text-white
    borderRadius: '4px', // rounded
    border: 'none',
    cursor: 'pointer',
  },
  uploadContentStyle: {
    marginBottom: '16px',
  },
  uploadInfoStyle: {
    marginBottom: '12px',
    fontWeight: '600',
    color: GRAY_700,
  },
  fileInputStyle: {
    marginBottom: '12px',
    padding: '8px',
    border: `1px solid ${GRAY_300}`,
    borderRadius: '4px',
    width: '100%',
  },
  uploadFileInfoStyle: {
    fontSize: '14px',
    color: GRAY_500,
    fontStyle: 'italic',
  },
  confirmButtonStyle: {
    padding: '8px 16px', // px-4 py-2
    backgroundColor: '#059669', // bg-green-600
    color: '#ffffff', // text-white
    borderRadius: '4px', // rounded
    border: 'none',
    cursor: 'pointer',
  },
  confirmButtonDisabledStyle: {
    padding: '8px 16px', // px-4 py-2
    backgroundColor: GRAY_300, // disabled gray
    color: GRAY_400, // disabled text
    borderRadius: '4px', // rounded
    border: 'none',
    cursor: 'not-allowed',
  },
  pageContainerStyle: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  } as React.CSSProperties,
  searchBarContainerStyle: {
    flexShrink: 0,
    backgroundColor: '#ffffff',
    borderBottom: `1px solid ${GRAY_300}`,
    paddingBottom: '8px',
  } as React.CSSProperties,
  tableContainerStyle: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
  } as React.CSSProperties,
  comingSoonContainerStyle: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: GRAY_50,
  } as React.CSSProperties,
  comingSoonTextStyle: {
    fontSize: '1.25rem',
    color: GRAY_500,
    fontWeight: 500,
  } as React.CSSProperties,
  productLoadingContainerStyle: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    minHeight: '180px',
    width: '384px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as React.CSSProperties,
};

export default ProductsReview;
